import {
  cancelGoalReminder,
  notificationAllowed,
  scheduleGoalReminder,
} from "@/lib/notifications";
import i18n from "@/i18n";
import type { NotificationPreferences, SocialGoal } from "@/types";

/** Days before a goal's due date that we nudge. */
const REMINDER_DAYS_BEFORE = 2;
/** Local hour (24h) the reminder fires on that day. */
const REMINDER_HOUR = 9;

/**
 * When a goal's reminder should fire, or null if it can't/shouldn't be armed:
 * the goal is archived/done, has no (or a malformed) due date, or the fire
 * time — two days before the due date at 9 AM local — is already in the past.
 */
function goalReminderTime(goal: SocialGoal): Date | null {
  if (goal.archived || goal.done || !goal.dueDate) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(goal.dueDate)) return null;

  const due = new Date(`${goal.dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;

  const when = new Date(due);
  when.setDate(when.getDate() - REMINDER_DAYS_BEFORE);
  when.setHours(REMINDER_HOUR, 0, 0, 0);

  if (when.getTime() <= Date.now()) return null;
  return when;
}

/**
 * Reconcile per-goal due-date reminders with the current goal list and the
 * user's notification preferences. Arms a one-shot local notification two days
 * before each open, dated goal's due date, and cancels reminders for goals that
 * are done, undated, past their reminder window, or when goal reminders are
 * muted. Safe to call whenever the goals or preferences change; the scheduling
 * primitive no-ops on web and without notification permission.
 */
export async function syncGoalReminders(
  goals: SocialGoal[],
  preferences: NotificationPreferences | null | undefined,
): Promise<void> {
  const allowed = notificationAllowed(preferences, "goalReminders");

  await Promise.all(
    goals.map(async (goal) => {
      const when = allowed ? goalReminderTime(goal) : null;
      if (when) {
        await scheduleGoalReminder(
          goal.id,
          when,
          i18n.t("growth.reminders.goalBody", {
            goal: goal.text,
            count: REMINDER_DAYS_BEFORE,
          }),
        );
      } else {
        await cancelGoalReminder(goal.id);
      }
    }),
  );
}
